import React, { useState } from 'react'
import { useMediaCore } from './hooks/useMediaCore'
import { MediaHeader } from './components/MediaHeader'
import { MediaContent } from './components/MediaContent'
import { MediaSidebar } from './components/MediaSidebar'
import { UploadDialog } from './components/UploadDialog'
import { NewFolderDialog } from './components/NewFolderDialog'
import { ConfirmDialog } from './components/ConfirmDialog'
import { MediaFile, MediaServiceConfig } from './types'

interface MediaLibraryPageProps {
  service: MediaServiceConfig
  resolveUrl: (path: string) => string
  onOptimize?: (file: MediaFile) => void
  onConvert?: (file: MediaFile) => void
}

export const MediaLibraryPage = ({
  service,
  resolveUrl,
  onOptimize,
  onConvert,
}: MediaLibraryPageProps) => {
  const core = useMediaCore({ service })
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid')

  const handleDeleteClick = (type: 'file' | 'folder', id: string, name: string) => {
    core.setDeleteTarget({ type, id, name })
    setDeleteConfirmOpen(true)
  }

  const handleConfirmDelete = async () => {
    if (!core.deleteTarget) return
    if (core.deleteTarget.type === 'file') {
      await core.handleDeleteFile(core.deleteTarget.id)
    } else {
      await core.handleDeleteFolder(core.deleteTarget.id)
    }
    core.setDeleteTarget(null)
  }

  const selectedFile = core.selectedFiles.length === 1 ? core.selectedFiles[0] : null

  return (
    <div className="flex flex-col h-full relative">
      <MediaHeader
        mode="page"
        currentFolderId={core.currentFolderId}
        currentFolderName={core.currentFolderName}
        folderCount={core.filteredFolders.length}
        fileCount={core.filteredFiles.length}
        searchTerm={core.searchTerm}
        canUpload={core.canUpload}
        canCreateFolder={core.canCreateFolder}
        onBack={() => core.navigateToFolder(null)}
        onUpload={() => core.setIsUploadOpen(true)}
        onNewFolder={() => core.setIsNewFolderOpen(true)}
        onSearchChange={core.setSearchTerm}
      />

      <div className="flex-1 flex overflow-hidden relative">
        <MediaContent
          isLoading={core.isLoading}
          folders={core.filteredFolders}
          files={core.filteredFiles}
          searchTerm={core.searchTerm}
          selectedFiles={core.selectedFiles}
          multiple={true}
          resolveUrl={resolveUrl}
          formatSize={core.formatSize}
          canDelete={core.canDelete}
          navigateToFolder={core.navigateToFolder}
          toggleSelect={core.toggleSelect}
          onDeleteFolder={(id, name) => handleDeleteClick('folder', id, name)}
          onDeleteFile={(id, name) => handleDeleteClick('file', id, name)}
          viewMode={viewMode}
        />

        {selectedFile && (
          <MediaSidebar
            selectedFile={selectedFile}
            resolveUrl={resolveUrl}
            formatSize={core.formatSize}
            onOptimize={onOptimize ? () => onOptimize(selectedFile) : undefined}
            onConvert={onConvert ? () => onConvert(selectedFile) : undefined}
          />
        )}
      </div>

      <UploadDialog
        open={core.isUploadOpen}
        onOpenChange={core.setIsUploadOpen}
        onUpload={core.handleUpload}
        disabled={!core.currentFolderId}
      />

      <NewFolderDialog
        open={core.isNewFolderOpen}
        onOpenChange={core.setIsNewFolderOpen}
        onCreate={core.handleCreateFolder}
      />

      <ConfirmDialog
        open={deleteConfirmOpen}
        onOpenChange={setDeleteConfirmOpen}
        title={`Delete ${core.deleteTarget?.type === 'file' ? 'file' : 'folder'}?`}
        description={`Are you sure you want to delete "${core.deleteTarget?.name}"? This action cannot be undone.`}
        onConfirm={handleConfirmDelete}
      />
    </div>
  )
}
